import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';

import Password from 'react-better-password';

export const stateDisabled = (theme: Theme) => {
  const colors = theme.colors;

  return css`
    &:read-only,
    &:disabled,
    &:disabled::placeholder {
      color: ${colors.DisabledInputColor};
      cursor: not-allowed;
      user-select: none;
      pointer-events: none;
    }
  `;
};

export const stateStyles = (theme: Theme) => {
  const colors = theme.colors;

  return css`
    border: 1px solid ${colors.LineStrong};
    outline: none;

    &:hover,
    &:focus {
      border: 1px solid ${colors.AccentNaplesYellow};
    }

    &.--error {
      border: 1px solid ${colors.error};
      &::placeholder {
        color: ${colors.error};
      }
      &:focus::placeholder,
      &:hover::placeholder {
        color: ${colors.SurfaceRomanSilver30};
      }
    }
  `;
};

export const commonStyles = (theme: Theme) => {
  const colors = theme.colors;

  return css`
    height: 36px;

    background-color: inherit;
    border-radius: ${theme.radii.card};
    padding: 8px 16px;
    color: ${colors.SurfaceRaisinBlack50};

    &.icon-left {
      padding-left: 46px;
    }
    &.icon-right {
      padding-right: 46px;
    }

    &::placeholder {
      color: ${colors.SurfaceRomanSilver30};
      font: inherit;
    }

    ${stateStyles(theme)}
    ${stateDisabled(theme)}
  `;
};

export const Input = styled.input`
  ${({ theme }) => commonStyles(theme)}
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  position: relative;
  height: 36px;
  background: transparent;
  transition:
    background-color 0.3s ease-in-out,
    border-color 0.2s ease-in-out;

  &[type='number'] {
    appearance: textfield;
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    padding-right: 24px;
  }

  &.input-mask {
    position: absolute;
    top: 0;
    left: 0;
    outline: none;
    visibility: hidden;
    border-color: transparent;

    &__visible {
      visibility: visible;
    }
  }
`;

export const PasswordInput = styled(Password)`
  ${({ theme }) => commonStyles(theme)}

  &.masked {
    color: ${({ theme }) => theme.colors.AccentNaplesYellow};
  }
`;

export const TextareaInput = styled.textarea`
  ${({ theme }) => commonStyles(theme)}

  & {
    resize: vertical;
  }
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.radii.card};
  background-color: ${({ theme }) => theme.colors.SurfaceCard};
  overflow: hidden;

  & .input {
    width: 100%;
  }

  &::after {
    position: absolute;
    top: -10px;
    right: -6px;
    content: '';
    display: none;
    width: 15px;
    height: 30px;
    transform: rotate(-45deg);
    transition: background-color 0.2s ease-in-out;
  }

  &.error::after {
    display: block;
    background-color: ${({ theme }) => theme.colors.error};
  }

  &.valid::after {
    display: block;
    background-color: ${({ theme }) => theme.colors.submit};
  }

  &:disabled,
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const IconContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  &.icon-left {
    left: 16px;
  }
  &.icon-right {
    right: 16px;
  }
  &.number-controls {
    right: 8px;
  }
`;
